import type { User } from '@owl/shared';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { Login } from './Login';
import { OwlLogo } from './shared';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'authenticated'; user: User }
  | { kind: 'error'; message: string };

type Props = {
  children: (
    user: User,
    onLogout: () => Promise<void>,
    onUserChange: (user: User) => void,
  ) => React.ReactNode;
};

export function AuthGate({ children }: Props) {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });

  const check = useCallback(async () => {
    try {
      const { user } = await api.me();
      setState({ kind: 'authenticated', user });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ kind: 'unauthenticated' });
      } else {
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to reach API',
        });
      }
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  if (state.kind === 'loading') {
    return (
      <div className="auth-loading">
        <OwlLogo size={36} />
        <div>Loading…</div>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="auth-loading">
        <OwlLogo size={36} />
        <div className="login-error" style={{ maxWidth: 360 }}>
          {state.message}
        </div>
        <button
          type="button"
          className="login-submit"
          style={{ maxWidth: 200 }}
          onClick={() => check()}
        >
          Retry
        </button>
      </div>
    );
  }
  if (state.kind === 'unauthenticated') {
    return <Login onSuccess={() => check()} />;
  }
  return (
    <>
      {children(
        state.user,
        async () => {
          await api.logout().catch(() => {});
          setState({ kind: 'unauthenticated' });
        },
        (user) => setState({ kind: 'authenticated', user }),
      )}
    </>
  );
}
